import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import type { GeoJSONSource, Map, MapLayerMouseEvent, Marker } from 'mapbox-gl';

import { MAPBOX_CONFIG } from './mapbox.config';
import { MapboxMapFactory, MapboxRuntime } from './mapbox-map.factory';
import { AoiBounds, PersistentAisHistoryOverlay, PersistentBolRouteOverlay, VesselMapPosition } from './vessel-map.models';
import { TutorialAnchorDirective } from 'src/app/core/tutorial/tutorial-anchor.directive';
import { SelectedVesselPosition } from 'src/app/core/models/selected-vessel-data.model';

const FALLBACK_CENTER: [number, number] = [58.405, 22.711];
const WORKSPACE_ZOOM = 6.3;
const MAX_AOI_RADIUS_NM = 50;
const AOI_OVERVIEW_MAX_ZOOM = 7;
const EARTH_RADIUS_NM = 3440.065;
type MapMode = 'live' | 'dark';
type VesselMapDataPanel = 'history' | 'route' | 'estimated' | null;

interface MapDataFeature {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: { type: 'Point'; coordinates: [number, number] } | { type: 'LineString'; coordinates: [number, number][] };
}

interface MapDataFeatureCollection {
  type: 'FeatureCollection';
  features: MapDataFeature[];
}

interface VesselWeatherSnapshot {
  time: string | null;
  temperatureCelsius: number | null;
  seaTemperatureCelsius: number | null;
  windSpeedKnots: number | null;
  windDirectionDegrees: number | null;
  waveHeightMeters: number | null;
  visibilityMeters: number | null;
  pressureHpa: number | null;
}

interface AreaSelection {
  height: number;
  left: number;
  top: number;
  width: number;
}

@Component({
  selector: 'app-vessel-map',
  standalone: true,
  imports: [CommonModule, TutorialAnchorDirective],
  templateUrl: './vessel-map.component.html',
  styleUrls: ['./vessel-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class VesselMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() position?: VesselMapPosition;
  @Input() positions: readonly VesselMapPosition[] = [];
  @Input() historyPoints: readonly SelectedVesselPosition[] = [];
  @Input() persistentHistoryOverlays: readonly PersistentAisHistoryOverlay[] = [];
  @Input() persistentBolRoutes: readonly PersistentBolRouteOverlay[] = [];
  @Input() routeData?: { distanceNm: number | null; coordinates: number[][] | null };
  @Input() estimatedPositionData?: { type: 'sat_e_estimate'; latitude: number | null; longitude: number | null };
  @Input() estimatedPositionUpdatedAt: string | null = null;
  @Input() weatherData?: VesselWeatherSnapshot;
  @Input() weatherLocation?: { latitude: number; longitude: number; label: string };
  @Input() selectedVesselName = '';
  @Input() selectedVesselImo = '';
  @Input() routeDestinationLabel = '';
  @Output() areaSelected = new EventEmitter<AoiBounds>();
  @Output() areaSearchRequested = new EventEmitter<AoiBounds>();
  @Output() darkShipAreaSelected = new EventEmitter<AoiBounds>();
  @Output() vesselSelected = new EventEmitter<string>();
  @ViewChild('mapContainer', { static: true }) private mapContainer!: ElementRef<HTMLElement>;

  isLoading = true;
  isReady = false;
  errorMessage = '';
  mapError = false;
  mapMode: MapMode = 'live';
  isSelectingArea = false;
  areaHint = '';
  areaSelection?: AreaSelection;
  areaBounds?: AoiBounds;
  areaSearchAvailable = false;
  activeDataPanel: VesselMapDataPanel = null;
  isWeatherOpen = false;
  weatherDetailsExpanded = false;
  historyFrom = '';
  historyTo = '';
  historyMessage = '';
  focusedHistoryPoint?: SelectedVesselPosition;

  private readonly config = inject(MAPBOX_CONFIG);
  private readonly factory = inject(MapboxMapFactory);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private map?: Map;
  private readonly markers = new globalThis.Map<string, { element: HTMLElement; marker: Marker }>();
  private runtime?: MapboxRuntime;
  private resizeObserver?: ResizeObserver;
  private lastRenderedPosition?: VesselMapPosition;
  private areaSelectionStart?: { x: number; y: number };
  private isDestroyed = false;
  private preserveViewportForImo?: string;
  private readonly historySourceId = 'selected-vessel-history';
  private readonly routeSourceId = 'selected-vessel-route';
  private readonly estimateSourceId = 'selected-vessel-estimate';
  private readonly weatherSourceId = 'selected-vessel-weather';
  private readonly persistentHistorySourceId = 'tracked-vessel-history-overlays';
  private readonly persistentBolRouteSourceId = 'tracked-bol-route-overlays';
  private readonly historyPointsLayerId = 'selected-vessel-history-points';
  private persistentHistoryOverlayImos = new Set<string>();

  private readonly handleHistoryPointClick = (event: MapLayerMouseEvent): void => {
    const properties = (event.features?.[0] as unknown as { properties?: Record<string, unknown> } | undefined)?.properties;
    if (!properties) return;
    const numericProperty = (name: string): number | null => {
      const value = properties[name];
      if (value === null || value === undefined || value === '') return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };
    this.focusedHistoryPoint = {
      latitude: numericProperty('latitude'),
      longitude: numericProperty('longitude'),
      speed: numericProperty('speed'),
      course: numericProperty('course'),
      heading: numericProperty('heading'),
      destination: String(properties['destination'] || '') || null,
      lastPositionUtc: String(properties['lastPositionUtc'] || '') || null
    };
    this.changeDetector.markForCheck();
  };

  private readonly handleLoad = (): void => {
    this.isLoading = false;
    this.isReady = true;
    this.mapError = false;
    this.errorMessage = '';
    this.syncMarkers();
    this.syncPosition(false);
    this.ensureDataLayers();
    this.syncDataLayers();
    this.syncPersistentHistoryOverlays(this.persistentHistoryOverlays.length > 0);
    this.syncPersistentBolRoutes();
    this.rememberPersistentHistoryOverlays();
    this.lastRenderedPosition = this.position ? { ...this.position } : undefined;
    this.map?.resize();
    this.changeDetector.markForCheck();
  };

  private readonly handleError = (): void => {
    if (this.isReady) {
      return;
    }

    this.isLoading = false;
    this.isReady = false;
    this.mapError = true;
    this.errorMessage = 'Map unavailable. Check the Mapbox token and network connection.';
    this.changeDetector.markForCheck();
  };

  ngAfterViewInit(): void {
    if (!this.isPublicToken(this.config.accessToken)) {
      this.isLoading = false;
      this.mapError = true;
      this.errorMessage = 'Mapbox token required. Add a public pk token to the local environment.';
      this.changeDetector.markForCheck();
      return;
    }

    const center = this.isValidPosition(this.position)
      ? [this.position.longitude, this.position.latitude] as [number, number]
      : FALLBACK_CENTER;

    void this.initializeMap(center);
  }

  private async initializeMap(center: [number, number]): Promise<void> {
    try {
      const runtime = await this.factory.load();
      if (this.isDestroyed) {
        return;
      }

      this.runtime = runtime;
      this.map = runtime.createMap({
        accessToken: this.config.accessToken,
        attributionControl: true,
        center,
        container: this.mapContainer.nativeElement,
        respectPrefersReducedMotion: true,
        style: this.config.styleUrl,
        zoom: WORKSPACE_ZOOM
      });
      this.map.on('style.load', this.handleLoad);
      this.map.on('error', this.handleError);
      this.observeContainerSize();
    } catch {
      if (!this.isDestroyed) {
        this.handleError();
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const vesselChanged = changes['selectedVesselImo']
      && !changes['selectedVesselImo'].firstChange
      && changes['selectedVesselImo'].previousValue !== changes['selectedVesselImo'].currentValue;
    if (vesselChanged) {
      this.closeDataPanel();
      this.closeWeather();
    }
    const weatherLocationChanged = changes['weatherLocation']
      && changes['weatherLocation'].previousValue
      && (changes['weatherLocation'].previousValue.latitude !== changes['weatherLocation'].currentValue?.latitude
        || changes['weatherLocation'].previousValue.longitude !== changes['weatherLocation'].currentValue?.longitude);
    if (weatherLocationChanged && this.isWeatherOpen) this.closeWeather();
    if ((changes['position'] || changes['positions']) && this.isReady && this.mapMode === 'live') {
      this.syncMarkers();
      this.syncPosition(true);
    }
    if ((changes['historyPoints'] || changes['routeData'] || changes['estimatedPositionData'] || changes['position']) && this.isReady) {
      this.ensureDataLayers();
      this.syncDataLayers();
    }
    if (changes['persistentHistoryOverlays'] && this.isReady) {
      this.syncPersistentHistoryOverlays(this.hasNewPersistentHistoryOverlay());
      this.rememberPersistentHistoryOverlays();
    }
    if (changes['persistentBolRoutes'] && this.isReady) {
      this.syncPersistentBolRoutes();
    }
    if (changes['weatherData'] && !this.weatherData) this.closeWeather();
  }

  zoomIn(): void {
    this.map?.zoomIn();
  }

  zoomOut(): void {
    this.map?.zoomOut();
  }

  showPastTrack(): void {
    if (!this.isReady || this.mapMode !== 'live') return;
    const dates = this.historyPoints
      .map(point => point.lastPositionUtc)
      .filter((value): value is string => Boolean(value))
      .map(value => value.slice(0, 10))
      .sort();
    this.historyFrom = dates[0] ?? '';
    this.historyTo = dates[dates.length - 1] ?? '';
    this.focusedHistoryPoint = undefined;
    this.historyMessage = dates.length ? '' : 'No sample positions available for this vessel.';
    this.isWeatherOpen = false;
    this.activeDataPanel = 'history';
    this.syncDataLayers();
    this.changeDetector.markForCheck();
  }

  showCalculatedRoute(): void {
    if (!this.isReady || this.mapMode !== 'live' || this.validRouteCoordinates().length < 2) return;
    this.activeDataPanel = 'route';
    this.isWeatherOpen = false;
    this.syncDataLayers();
    this.fitMap(this.validRouteCoordinates());
    this.changeDetector.markForCheck();
  }

  focusBolRoute(routeId: number): void {
    const route = this.persistentBolRoutes.find(item => item.id === routeId);
    if (!this.isReady || !route || !route.visible) return;
    const coordinates = route.coordinates.filter(coordinate => this.validCoordinate(coordinate));
    if (coordinates.length > 1) this.fitMap(coordinates);
  }

  showEstimatedPosition(): void {
    if (!this.isReady || this.mapMode !== 'live' || !this.validEstimate()) return;
    this.activeDataPanel = 'estimated';
    this.isWeatherOpen = false;
    this.syncDataLayers();
    const estimate = this.estimatedPositionData!;
    const points: [number, number][] = [[estimate.longitude as number, estimate.latitude as number]];
    if (this.isValidPosition(this.position)) points.push([this.position.longitude, this.position.latitude]);
    this.fitMap(points);
    this.changeDetector.markForCheck();
  }

  showWeather(): void {
    if (!this.isReady || this.mapMode !== 'live' || !this.weatherData || !this.isValidWeatherLocation()) return;
    this.activeDataPanel = null;
    this.weatherDetailsExpanded = false;
    this.isWeatherOpen = true;
    this.syncDataLayers();
    this.setSourceData(this.weatherSourceId, {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [this.weatherLocation!.longitude, this.weatherLocation!.latitude] } }]
    });
    this.fitMap([[this.weatherLocation!.longitude, this.weatherLocation!.latitude]]);
    this.changeDetector.markForCheck();
  }

  closeDataPanel(): void {
    this.activeDataPanel = null;
    this.focusedHistoryPoint = undefined;
    this.historyMessage = '';
    if (this.map) this.syncDataLayers();
    this.changeDetector.markForCheck();
  }

  closeWeather(): void {
    this.isWeatherOpen = false;
    this.weatherDetailsExpanded = false;
    if (this.map) this.setSourceData(this.weatherSourceId, this.emptyFeatureCollection());
    this.changeDetector.markForCheck();
  }

  updateHistoryFrom(event: Event): void {
    this.historyFrom = (event.target as HTMLInputElement).value;
  }

  updateHistoryTo(event: Event): void {
    this.historyTo = (event.target as HTMLInputElement).value;
  }

  applyHistoryRange(): void {
    if (!this.historyFrom || !this.historyTo || this.historyFrom > this.historyTo) {
      this.historyMessage = 'Choose an end date on or after the start date.';
      this.setSourceData(this.historySourceId, this.emptyFeatureCollection());
      this.changeDetector.markForCheck();
      return;
    }
    const points = this.filteredHistoryPoints();
    if (!points.length) {
      this.historyMessage = 'No sample positions available for these dates.';
      this.setSourceData(this.historySourceId, this.emptyFeatureCollection());
      this.changeDetector.markForCheck();
      return;
    }
    const features: MapDataFeature[] = [];
    if (points.length > 1) {
      features.push({
        type: 'Feature',
        properties: { kind: 'track' },
        geometry: { type: 'LineString', coordinates: points.map(point => [point.longitude as number, point.latitude as number] as [number, number]) }
      });
    }
    points.forEach(point => features.push({
      type: 'Feature',
      properties: {
        latitude: point.latitude,
        longitude: point.longitude,
        speed: point.speed,
        course: point.course,
        heading: point.heading,
        destination: point.destination,
        lastPositionUtc: point.lastPositionUtc
      },
      geometry: { type: 'Point', coordinates: [point.longitude as number, point.latitude as number] }
    }));
    this.historyMessage = `${points.length} sample positions · select a point for time, speed and course.`;
    this.focusedHistoryPoint = undefined;
    this.setSourceData(this.historySourceId, { type: 'FeatureCollection', features });
    this.fitMap(points.map(point => [point.longitude as number, point.latitude as number]));
    this.changeDetector.markForCheck();
  }

  formatMapValue(value: number | null | undefined, suffix = '', decimals = 1): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return 'Not reported';
    return `${Number(value).toLocaleString('en-GB', { maximumFractionDigits: decimals })}${suffix}`;
  }

  formatMapDate(value: string | null | undefined): string {
    if (!value) return 'Not reported';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC'
    }).format(date) + ' UTC';
  }

  setMapMode(mode: MapMode): void {
    if (!this.isReady || this.mapMode === mode) {
      return;
    }

    this.mapMode = mode;
    this.closeDataPanel();
    this.closeWeather();
    this.areaSelection = undefined;
    this.areaBounds = undefined;
    this.areaSearchAvailable = false;
    this.areaHint = '';

    if (mode === 'dark') {
      this.clearMarkers();
      this.fitMaximumAreaEnvelope();
      this.startAreaSelection();
    } else {
      this.isSelectingArea = false;
      this.syncMarkers();
      this.syncPosition(false);
    }

    this.changeDetector.markForCheck();
  }

  startAreaSelection(): void {
    if (!this.isReady) {
      return;
    }

    this.isSelectingArea = true;
    this.areaSelection = undefined;
    this.areaBounds = undefined;
    this.areaSearchAvailable = false;
    this.fitMaximumAreaEnvelope();
    this.areaHint = this.mapMode === 'dark'
      ? 'Draw the Dark Ship Area of Interest on the map.'
      : 'Drag a rectangle to define the live vessel search area.';
    this.changeDetector.markForCheck();
  }

  resetAreaSelection(): void {
    this.areaSelectionStart = undefined;
    this.isSelectingArea = false;
    this.areaSelection = undefined;
    this.areaBounds = undefined;
    this.areaSearchAvailable = false;
    this.areaHint = '';

    this.changeDetector.markForCheck();
  }

  preserveViewportForVessel(imo: string | null | undefined): void {
    const normalizedImo = this.normalizeVesselImo(imo);
    if (normalizedImo) this.preserveViewportForImo = normalizedImo;
  }

  beginAreaDrag(event: PointerEvent): void {
    const point = this.getRelativePoint(event);
    if (!point) {
      return;
    }

    this.areaSelectionStart = point;
    this.areaSelection = { left: point.x, top: point.y, width: 0, height: 0 };
    (event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId);
  }

  updateAreaDrag(event: PointerEvent): void {
    if (!this.areaSelectionStart) {
      return;
    }

    const point = this.getRelativePoint(event);
    if (!point) {
      return;
    }

    // Clamp the visible rectangle continuously so it never exceeds the AOI
    // envelope that can be sent to Datalastic.
    this.areaSelection = this.clampAreaSelection(this.toAreaSelection(this.areaSelectionStart, point));
  }

  completeAreaDrag(event: PointerEvent): void {
    if (!this.areaSelectionStart) {
      return;
    }

    this.updateAreaDrag(event);
    this.areaSelectionStart = undefined;
    this.isSelectingArea = false;
    const bounds = this.getClampedBounds();
    if (!bounds) {
      this.areaSelection = undefined;
      this.areaHint = 'Select a valid geographic area within the map.';
      this.changeDetector.markForCheck();
      return;
    }

    this.areaBounds = bounds;
    this.areaSearchAvailable = false;
    this.areaHint = this.mapMode === 'dark'
      ? 'Area of Interest selected. Review the coordinates to request Dark Ship analysis.'
      : '';
    this.areaSelected.emit(bounds);
    if (this.mapMode === 'dark') {
      this.darkShipAreaSelected.emit(bounds);
    } else {
      this.areaSearchRequested.emit(bounds);
    }
    this.changeDetector.markForCheck();
  }

  private ensureDataLayers(): void {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const ensureSource = (id: string): void => {
      if (!this.map?.getSource(id)) this.map?.addSource(id, { type: 'geojson', data: this.emptyFeatureCollection() });
    };
    ensureSource(this.historySourceId);
    ensureSource(this.routeSourceId);
    ensureSource(this.estimateSourceId);
    ensureSource(this.weatherSourceId);
    ensureSource(this.persistentHistorySourceId);
    ensureSource(this.persistentBolRouteSourceId);
    if (!this.map.getLayer('selected-vessel-history-line')) this.map.addLayer({
      id: 'selected-vessel-history-line', type: 'line', source: this.historySourceId,
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: { 'line-color': '#a2afff', 'line-width': 2.5, 'line-opacity': 0.92 }
    });
    if (!this.map.getLayer(this.historyPointsLayerId)) this.map.addLayer({
      id: this.historyPointsLayerId, type: 'circle', source: this.historySourceId,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 4, 'circle-color': '#6c83d6', 'circle-stroke-color': '#ccd2ff', 'circle-stroke-width': 1 }
    });
    if (!this.map.getLayer('tracked-vessel-history-overlays-line')) this.map.addLayer({
      id: 'tracked-vessel-history-overlays-line', type: 'line', source: this.persistentHistorySourceId,
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#4fe3d1'],
        'line-width': 2.7,
        'line-opacity': 0.94
      }
    });
    if (!this.map.getLayer('tracked-bol-route-overlays-line')) this.map.addLayer({
      id: 'tracked-bol-route-overlays-line', type: 'line', source: this.persistentBolRouteSourceId,
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#e9c16f'],
        'line-width': 3,
        'line-dasharray': [2, 1.5],
        'line-opacity': 0.95
      }
    });
    if (!this.map.getLayer('selected-vessel-route-line')) this.map.addLayer({
      id: 'selected-vessel-route-line', type: 'line', source: this.routeSourceId,
      paint: { 'line-color': '#e9c16f', 'line-width': 3, 'line-dasharray': [2, 1.7], 'line-opacity': 0.95 }
    });
    if (!this.map.getLayer('selected-vessel-estimate-line')) this.map.addLayer({
      id: 'selected-vessel-estimate-line', type: 'line', source: this.estimateSourceId,
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: { 'line-color': '#efc479', 'line-width': 1.5, 'line-dasharray': [1, 2], 'line-opacity': 0.9 }
    });
    if (!this.map.getLayer('selected-vessel-estimate-point')) this.map.addLayer({
      id: 'selected-vessel-estimate-point', type: 'circle', source: this.estimateSourceId,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: { 'circle-radius': 8, 'circle-color': '#f3cd84', 'circle-opacity': 0.35, 'circle-stroke-color': '#f3cd84', 'circle-stroke-width': 2 }
    });
    if (!this.map.getLayer('selected-vessel-estimate-label')) this.map.addLayer({
      id: 'selected-vessel-estimate-label', type: 'symbol', source: this.estimateSourceId,
      filter: ['==', ['get', 'kind'], 'sat-e'],
      layout: {
        'text-field': 'SAT-E · Estimated position',
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Regular'],
        'text-size': 10,
        'text-anchor': 'left',
        'text-offset': [1.2, 0],
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: { 'text-color': '#dceff7', 'text-halo-color': '#031724', 'text-halo-width': 1.5 }
    });
    if (!this.map.getLayer('selected-vessel-weather-point')) this.map.addLayer({
      id: 'selected-vessel-weather-point', type: 'circle', source: this.weatherSourceId,
      paint: { 'circle-radius': 18, 'circle-color': '#42c9e8', 'circle-opacity': 0.12, 'circle-stroke-color': '#a6e0f3', 'circle-stroke-width': 1, 'circle-stroke-opacity': 0.95 }
    });
    this.map.off('click', this.historyPointsLayerId, this.handleHistoryPointClick);
    this.map.off('mouseenter', this.historyPointsLayerId, this.setPointerCursor);
    this.map.off('mouseleave', this.historyPointsLayerId, this.resetPointerCursor);
    this.map.on('click', this.historyPointsLayerId, this.handleHistoryPointClick);
    this.map.on('mouseenter', this.historyPointsLayerId, this.setPointerCursor);
    this.map.on('mouseleave', this.historyPointsLayerId, this.resetPointerCursor);
  }

  private readonly setPointerCursor = (): void => { if (this.map) this.map.getCanvas().style.cursor = 'pointer'; };
  private readonly resetPointerCursor = (): void => { if (this.map) this.map.getCanvas().style.cursor = ''; };

  private syncDataLayers(): void {
    if (!this.map || !this.map.isStyleLoaded()) return;
    this.ensureDataLayers();
    if (this.activeDataPanel === 'history') {
      if (this.historyFrom && this.historyTo) this.applyHistoryRange();
      else this.setSourceData(this.historySourceId, this.emptyFeatureCollection());
    } else {
      this.setSourceData(this.historySourceId, this.emptyFeatureCollection());
    }
    const route = this.activeDataPanel === 'route' ? this.validRouteCoordinates() : [];
    this.setSourceData(this.routeSourceId, route.length > 1 ? {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: route } }]
    } : this.emptyFeatureCollection());

    const estimate = this.activeDataPanel === 'estimated' && this.validEstimate() ? this.estimatedPositionData : undefined;
    const estimateFeatures: MapDataFeature[] = [];
    if (estimate) {
      if (this.isValidPosition(this.position)) estimateFeatures.push({
        type: 'Feature', properties: { kind: 'last-ais-position' },
        geometry: { type: 'LineString', coordinates: [[this.position.longitude, this.position.latitude], [estimate.longitude as number, estimate.latitude as number]] }
      });
      estimateFeatures.push({ type: 'Feature', properties: { kind: 'sat-e' }, geometry: { type: 'Point', coordinates: [estimate.longitude as number, estimate.latitude as number] } });
    }
    this.setSourceData(this.estimateSourceId, { type: 'FeatureCollection', features: estimateFeatures });
    if (!this.isWeatherOpen) this.setSourceData(this.weatherSourceId, this.emptyFeatureCollection());
  }

  private syncPersistentHistoryOverlays(fitNewest: boolean): void {
    if (!this.map || !this.map.isStyleLoaded()) return;
    this.ensureDataLayers();
    const features: MapDataFeature[] = [];
    const validCoordinates: [number, number][] = [];
    this.persistentHistoryOverlays.forEach(overlay => overlay.tracks.forEach(track => {
      const coordinates = track.positions
        .filter(position => Number.isFinite(position.latitude) && Number.isFinite(position.longitude)
          && Math.abs(position.latitude) <= 90 && Math.abs(position.longitude) <= 180)
        .map(position => [position.longitude, position.latitude] as [number, number]);
      if (coordinates.length < 2) return;
      features.push({
        type: 'Feature',
        properties: { imo: overlay.imo, name: overlay.name, color: overlay.color, trackId: track.id },
        geometry: { type: 'LineString', coordinates }
      });
      validCoordinates.push(...coordinates);
    }));
    this.setSourceData(this.persistentHistorySourceId, { type: 'FeatureCollection', features });
    if (fitNewest && validCoordinates.length) this.fitMap(validCoordinates);
  }

  private syncPersistentBolRoutes(): void {
    if (!this.map || !this.map.isStyleLoaded()) return;
    this.ensureDataLayers();
    const features: MapDataFeature[] = this.persistentBolRoutes
      .filter(route => route.visible)
      .map(route => ({
        type: 'Feature',
        properties: { bolId: route.id, billOfLadingNumber: route.billOfLadingNumber, color: route.color },
        geometry: { type: 'LineString', coordinates: route.coordinates.filter(coordinate => this.validCoordinate(coordinate)) }
      } as MapDataFeature))
      .filter(feature => feature.geometry.coordinates.length > 1);
    this.setSourceData(this.persistentBolRouteSourceId, { type: 'FeatureCollection', features });
  }

  private validCoordinate(coordinate: unknown): coordinate is [number, number] {
    return Array.isArray(coordinate) && Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1])
      && Math.abs(coordinate[0]) <= 180 && Math.abs(coordinate[1]) <= 90;
  }

  private hasNewPersistentHistoryOverlay(): boolean {
    return this.persistentHistoryOverlays.some(overlay => !this.persistentHistoryOverlayImos.has(overlay.imo));
  }

  private rememberPersistentHistoryOverlays(): void {
    this.persistentHistoryOverlayImos = new Set(this.persistentHistoryOverlays.map(overlay => overlay.imo));
  }

  private setSourceData(id: string, data: MapDataFeatureCollection): void {
    const source = this.map?.getSource(id) as GeoJSONSource | undefined;
    source?.setData(data as Parameters<GeoJSONSource['setData']>[0]);
  }

  private emptyFeatureCollection(): MapDataFeatureCollection {
    return { type: 'FeatureCollection', features: [] };
  }

  private filteredHistoryPoints(): SelectedVesselPosition[] {
    return this.historyPoints.filter(point => {
      const day = point.lastPositionUtc?.slice(0, 10);
      return Boolean(day && day >= this.historyFrom && day <= this.historyTo
        && Number.isFinite(point.latitude) && Number.isFinite(point.longitude)
        && (point.latitude as number) >= -90 && (point.latitude as number) <= 90
        && (point.longitude as number) >= -180 && (point.longitude as number) <= 180);
    }).sort((left, right) => (left.lastPositionUtc || '').localeCompare(right.lastPositionUtc || ''));
  }

  private validRouteCoordinates(): [number, number][] {
    return (this.routeData?.coordinates ?? []).filter((coordinate): coordinate is [number, number] =>
      Array.isArray(coordinate) && Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1])
      && Math.abs(coordinate[0]) <= 180 && Math.abs(coordinate[1]) <= 90);
  }

  private validEstimate(): boolean {
    return Number.isFinite(this.estimatedPositionData?.latitude)
      && Number.isFinite(this.estimatedPositionData?.longitude)
      && Math.abs(this.estimatedPositionData?.latitude as number) <= 90
      && Math.abs(this.estimatedPositionData?.longitude as number) <= 180;
  }

  private isValidWeatherLocation(): boolean {
    return Number.isFinite(this.weatherLocation?.latitude) && Number.isFinite(this.weatherLocation?.longitude)
      && Math.abs(this.weatherLocation?.latitude as number) <= 90
      && Math.abs(this.weatherLocation?.longitude as number) <= 180;
  }

  private fitMap(coordinates: [number, number][]): void {
    if (!this.map || !coordinates.length) return;
    if (coordinates.length === 1) {
      this.map.flyTo({ center: coordinates[0], duration: 700, essential: true, zoom: Math.max(this.map.getZoom(), 7) });
      return;
    }
    const lngs = coordinates.map(([longitude]) => longitude);
    const lats = coordinates.map(([, latitude]) => latitude);
    this.map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], {
      padding: { top: 70, right: 90, bottom: 70, left: 90 }, maxZoom: 9, duration: 700
    });
  }

  searchSelectedArea(): void {
    if (this.mapMode === 'live' && this.areaBounds && this.areaSearchAvailable) {
      const bounds = this.areaBounds;
      this.areaSearchAvailable = false;
      this.areaHint = '';
      this.changeDetector.markForCheck();
      this.areaSearchRequested.emit(bounds);
    }
  }

  ngOnDestroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.isDestroyed = true;
    this.resizeObserver?.disconnect();
    this.clearMarkers();
    if (this.map) {
      this.map.off('style.load', this.handleLoad);
      this.map.off('error', this.handleError);
      this.map.remove();
    }
  }

  private syncPosition(animate: boolean): void {
    if (!this.map || !this.runtime) {
      return;
    }

    if (!this.isValidPosition(this.position)) {
      if (!this.position) {
        this.errorMessage = '';
        this.lastRenderedPosition = undefined;
        this.changeDetector.markForCheck();
        return;
      }
      this.mapError = false;
      this.errorMessage = 'Vessel position unavailable. Select a vessel with valid coordinates.';
      this.lastRenderedPosition = undefined;
      this.changeDetector.markForCheck();
      return;
    }

    this.errorMessage = '';
    const center: [number, number] = [this.position.longitude, this.position.latitude];
    const preserveViewport = this.preserveViewportForImo === this.normalizeVesselImo(this.position.imo);
    if (preserveViewport) this.preserveViewportForImo = undefined;

    this.syncMarkers();

    const coordinatesChanged = !this.lastRenderedPosition
      || this.lastRenderedPosition.longitude !== this.position.longitude
      || this.lastRenderedPosition.latitude !== this.position.latitude;

    if (coordinatesChanged && !preserveViewport) {
      if (!animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        this.map.jumpTo({ center, zoom: WORKSPACE_ZOOM });
      } else {
        this.map.flyTo({ center, duration: 1100, essential: true, zoom: WORKSPACE_ZOOM });
      }
    }

    this.lastRenderedPosition = { ...this.position };
    this.changeDetector.markForCheck();
  }

  private isPublicToken(token: string): boolean {
    return token.trim().startsWith('pk.');
  }

  private normalizeHeading(heading: number | undefined): number {
    if (heading === undefined || !Number.isFinite(heading)) {
      return 0;
    }

    return ((heading % 360) + 360) % 360;
  }

  private normalizeVesselImo(imo: string | null | undefined): string {
    return String(imo ?? '').replace(/\D/g, '');
  }

  private isValidPosition(position: VesselMapPosition | undefined): position is VesselMapPosition {
    return Boolean(
      position
      && Number.isFinite(position.longitude)
      && Number.isFinite(position.latitude)
      && position.longitude >= -180
      && position.longitude <= 180
      && position.latitude >= -90
      && position.latitude <= 90
    );
  }

  private observeContainerSize(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.map?.resize());
    this.resizeObserver.observe(this.mapContainer.nativeElement);
  }

  private getRelativePoint(event: PointerEvent): { x: number; y: number } | undefined {
    const bounds = this.mapContainer.nativeElement.getBoundingClientRect();
    if (!bounds.width || !bounds.height) {
      return undefined;
    }

    return {
      x: Math.min(Math.max(event.clientX - bounds.left, 0), bounds.width),
      y: Math.min(Math.max(event.clientY - bounds.top, 0), bounds.height)
    };
  }

  private getClampedBounds(): AoiBounds | undefined {
    if (!this.map || !this.areaSelection) {
      return undefined;
    }

    this.areaSelection = this.clampAreaSelection(this.areaSelection);
    const bounds = this.selectionToBounds(this.areaSelection);
    return bounds ? this.toBounds(bounds.west, bounds.south, bounds.east, bounds.north) : undefined;
  }

  private clampAreaSelection(selection: AreaSelection): AreaSelection {
    const bounds = this.selectionToBounds(selection);
    if (!bounds || this.aoiRadius(bounds) <= MAX_AOI_RADIUS_NM) {
      return selection;
    }

    // Keep the initial point anchored as the top-left corner and find the
    // largest square whose geographic corner radius remains within 50 NM.
    let low = 0;
    let high = Math.min(selection.width, selection.height);
    for (let iteration = 0; iteration < 24; iteration += 1) {
      const side = (low + high) / 2;
      const candidate: AreaSelection = { left: selection.left, top: selection.top, width: side, height: side };
      const candidateBounds = this.selectionToBounds(candidate);
      if (candidateBounds && this.aoiRadius(candidateBounds) <= MAX_AOI_RADIUS_NM) {
        low = side;
      } else {
        high = side;
      }
    }
    return { left: selection.left, top: selection.top, width: low, height: low };
  }

  private selectionToBounds(selection: AreaSelection): AoiBounds | undefined {
    if (!this.map || !selection.width || !selection.height) {
      return undefined;
    }
    const start = this.map.unproject([selection.left, selection.top]);
    const end = this.map.unproject([
      selection.left + selection.width,
      selection.top + selection.height
    ]);
    return this.toBounds(start.lng, start.lat, end.lng, end.lat);
  }

  private toBounds(firstLng: number, firstLat: number, secondLng: number, secondLat: number): AoiBounds | undefined {
    // Rectangles crossing the anti-meridian are intentionally unsupported by the API contract.
    if (Math.abs(firstLng - secondLng) > 180) {
      return undefined;
    }
    const bounds = {
      west: Math.min(firstLng, secondLng),
      south: Math.min(firstLat, secondLat),
      east: Math.max(firstLng, secondLng),
      north: Math.max(firstLat, secondLat)
    };
    return bounds.west < bounds.east && bounds.south < bounds.north
      && bounds.west >= -180 && bounds.east <= 180
      && bounds.south >= -90 && bounds.north <= 90
      && bounds.west < bounds.east
      ? bounds
      : undefined;
  }

  private aoiRadius(bounds: AoiBounds): number {
    const center = { latitude: (bounds.south + bounds.north) / 2, longitude: (bounds.west + bounds.east) / 2 };
    return Math.max(...[
      { latitude: bounds.south, longitude: bounds.west },
      { latitude: bounds.south, longitude: bounds.east },
      { latitude: bounds.north, longitude: bounds.west },
      { latitude: bounds.north, longitude: bounds.east }
    ].map(corner => this.distanceNm(center, corner)));
  }

  private distanceNm(from: { latitude: number; longitude: number }, to: { latitude: number; longitude: number }): number {
    const radians = (degrees: number): number => degrees * Math.PI / 180;
    const latitudeDelta = radians(to.latitude - from.latitude);
    const longitudeDelta = radians(to.longitude - from.longitude);
    const a = Math.sin(latitudeDelta / 2) ** 2
      + Math.cos(radians(from.latitude)) * Math.cos(radians(to.latitude)) * Math.sin(longitudeDelta / 2) ** 2;
    return EARTH_RADIUS_NM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  private fitMaximumAreaEnvelope(): void {
    if (!this.map) {
      return;
    }

    const mapCenter = this.map.getCenter();
    // AOI selection should preserve the current map context, even after a vessel
    // has been selected; do not snap back to the vessel's live position.
    const center = { latitude: mapCenter.lat, longitude: mapCenter.lng };
    const latitudeDelta = MAX_AOI_RADIUS_NM / 60;
    const longitudeDelta = MAX_AOI_RADIUS_NM / (60 * Math.max(Math.cos(center.latitude * Math.PI / 180), 0.2));
    this.map.fitBounds([
      [center.longitude - longitudeDelta, center.latitude - latitudeDelta],
      [center.longitude + longitudeDelta, center.latitude + latitudeDelta]
    ], { padding: 48, maxZoom: AOI_OVERVIEW_MAX_ZOOM });
  }

  private toAreaSelection(
    start: { x: number; y: number },
    end: { x: number; y: number }
  ): AreaSelection {
    const side = Math.max(0, Math.min(end.x - start.x, end.y - start.y));
    return {
      height: side,
      left: start.x,
      top: start.y,
      width: side
    };
  }

  private syncMarkers(): void {
    if (!this.map || !this.runtime || this.mapMode !== 'live') {
      return;
    }

    const positions = this.positions.length ? this.positions : (this.position ? [this.position] : []);
    const activeKeys = new Set(positions.filter(position => this.isValidPosition(position)).map(position => position.imo));
    for (const [imo, entry] of this.markers) {
      if (!activeKeys.has(imo)) {
        entry.marker.remove();
        this.markers.delete(imo);
      }
    }

    for (const vessel of positions) {
      if (!this.isValidPosition(vessel)) {
        continue;
      }

      let entry = this.markers.get(vessel.imo);
      if (!entry) {
        const element = document.createElement('button');
        element.type = 'button';
        element.className = 'vessel-map__marker-wrap';
        element.innerHTML = `<div class="vessel-map__ship-marker"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 14 8-10 8 10-2 5H6Z"></path><path d="M12 4v15M7 14h10"></path></svg></div>`;
        element.addEventListener('click', () => this.vesselSelected.emit(vessel.imo));
        const marker = this.runtime.createMarker({ anchor: 'center', element, pitchAlignment: 'viewport', rotationAlignment: 'viewport' });
        marker.setLngLat([vessel.longitude, vessel.latitude]);
        marker.setRotation(this.normalizeHeading(vessel.heading));
        marker.addTo(this.map);
        entry = { element, marker };
        this.markers.set(vessel.imo, entry);
      }

      entry.element.setAttribute('aria-label', `Select vessel ${vessel.name}`);
      entry.element.querySelector('.vessel-map__ship-marker')?.classList.toggle(
        'vessel-map__ship-marker--selected', vessel.imo === this.position?.imo
      );
      entry.marker.setLngLat([vessel.longitude, vessel.latitude]);
      entry.marker.setRotation(this.normalizeHeading(vessel.heading));
    }
  }

  private clearMarkers(): void {
    for (const entry of this.markers.values()) {
      entry.marker.remove();
    }
    this.markers.clear();
  }
}
